'use client';

import dynamic from 'next/dynamic';
import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useSupabaseFromContext } from './SupabaseClientContext';

const JoditEditorImport = dynamic(() => import('jodit-react'), { ssr: false });

const BUCKET_NAME = 'embed-files';

// --- HELPERS ---

function isVideoUrl(text: string): boolean {
  try {
    const url = new URL(text.trim());
    return /\.(mp4|webm|ogg|mov|m4v)$/i.test(url.pathname);
  } catch (e) {
    return false;
  }
}

function isImageUrl(text: string): boolean {
  try {
    const url = new URL(text.trim());
    return /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff)$/i.test(url.pathname);
  } catch (e) {
    return false;
  }
}

async function generateQuickHash(file: File): Promise<string> {
  const chunkSize = 4096;
  const chunks: Blob[] = [];

  chunks.push(new Blob([new TextEncoder().encode(`${file.name}-${file.size}-${file.type}`)]));
  chunks.push(file.slice(0, chunkSize));

  if (file.size > chunkSize * 2) {
    const mid = Math.floor(file.size / 2);
    chunks.push(file.slice(mid, mid + chunkSize));
  }

  if (file.size > chunkSize) {
    chunks.push(file.slice(file.size - chunkSize, file.size));
  }

  const combinedBlob = new Blob(chunks);
  const buffer = await combinedBlob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashBase64 = btoa(String.fromCharCode(...hashArray));
  
  return hashBase64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
    .substring(0, 11);
}

// --- COMPONENT ---

export const JoditEditor = ({
  value,
  onChange
}: {
  value?: string;
  onChange?: (data: string) => void;
}) => {
  const joditInstance = useRef<any>(null);
  const supabase = useSupabaseFromContext();
  
  // Ref to hold the sync handler
  const pasteHandlerRef = useRef<((e: ClipboardEvent) => void | boolean) | null>(null);

  const uploadFileToSupabase = useCallback(
    async (file: File): Promise<string | null> => {
      try {
        const id = await generateQuickHash(file);
        const extension = file.name.split('.').pop()?.toLowerCase() || '';
        const path = `${id}.${extension}`;

        const { data: publicData } = supabase.storage
          .from(BUCKET_NAME)
          .getPublicUrl(path);

        const publicUrl = publicData.publicUrl;

        try {
          const checkRes = await fetch(publicUrl, { method: 'HEAD' });
          if (checkRes.ok) return publicUrl;
        } catch (e) { /* ignore */ }

        console.log('[UPLOAD] Uploading:', path);
        const { error: uploadError } = await supabase.storage
          .from(BUCKET_NAME)
          .upload(path, file, { upsert: false });

        if (uploadError) {
          if (uploadError.message.toLowerCase().includes('duplicate') || uploadError.message.toLowerCase().includes('exists')) {
             return publicUrl;
          }
          console.error('[UPLOAD ERROR]', uploadError.message);
          return null;
        }

        return publicUrl;
      } catch (err) {
        console.error('Unexpected upload error', err);
        return null;
      }
    },
    [supabase]
  );

  // This function handles the async logic detached from the event loop
  const processPasteData = useCallback(async (text: string, files: File[]) => {
      const editor = joditInstance.current;
      if (!editor) return;

      // --- 1. HANDLE TEXT URL (Video/Image) ---
      if (files.length === 0 && text) {
        const trimmedText = text.trim();

        if (isVideoUrl(trimmedText)) {
          editor.s.insertHTML(
            `<div style="width: 400px; max-width: 100%;"><video controls src="${trimmedText}" style="width: 100%; height: auto; display: block;"></video></div><p><br/></p>`
          );
          return;
        }

        if (isImageUrl(trimmedText)) {
          editor.s.insertHTML(
            `<img src="${trimmedText}" alt="" style="width: 300px; max-width: 100%;" />`
          );
          return;
        }
      }

      // --- 2. HANDLE FILES ---
      if (files.length > 0) {
        for (const file of files) {
          const placeholderId = `loader-${Math.random().toString(36).substring(7)}`;
          
          editor.s.insertHTML(
            `<span id="${placeholderId}" style="color: #888; font-style: italic;">[Uploading ${file.name}...]</span>`
          );

          const url = await uploadFileToSupabase(file);

          const placeholderEl = editor.editor.querySelector(`#${placeholderId}`);
          if (placeholderEl) {
            editor.s.select(placeholderEl);
          }

          if (!url) {
            if (placeholderEl) editor.s.insertHTML('');
            continue;
          }

          try {
            if (file.type.startsWith('image/')) {
              editor.s.insertHTML(
                `<img src="${url}" alt="" style="width: 300px; max-width: 100%;" />`
              );
            } else if (file.type.startsWith('video/')) {
              editor.s.insertHTML(
                `<div style="width: 400px; max-width: 100%;"><video controls src="${url}" style="width: 100%; height: auto; display: block;"></video></div><p><br/></p>`
              );
            } else {
              editor.s.insertHTML(
                `<a href="${url}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; color: blue;">${file.name}</a>`
              );
            }
          } catch (err) {
            console.error('INSERT ERROR:', err);
          }
        }
      }
  }, [uploadFileToSupabase]);


  // --- SYNCHRONOUS HANDLER ---
  const handlePasteSync = useCallback((e: ClipboardEvent) => {
      const clipboard = e.clipboardData;
      if (!clipboard) return;

      const text = clipboard.getData('text/plain') || '';
      const files = Array.from(clipboard.files || []);

      const isMediaUrl = text && (isVideoUrl(text) || isImageUrl(text));
      const hasFiles = files.length > 0;

      // If matches our criteria, STOP EVERYTHING and process manually
      if (isMediaUrl || hasFiles) {
        e.preventDefault();
        e.stopPropagation();
        
        // Pass data to async function (detached)
        processPasteData(text, files);

        // CLEANUP: If Jodit accidentally inserted text before we caught it
        // We select the current node and clear it if it matches the URL
        const editor = joditInstance.current;
        if(editor && isMediaUrl) {
            // Slight delay to allow Jodit to finish its cycle if it leaked
            setTimeout(() => {
                const current = editor.s.current();
                if(current && current.nodeType === 3 && current.textContent?.trim() === text.trim()) {
                    current.textContent = '';
                }
            }, 10);
        }
        
        // RETURN FALSE IS CRITICAL: It tells Jodit "I handled this, do nothing"
        return false; 
      }
  }, [processPasteData]);


  useEffect(() => {
    pasteHandlerRef.current = handlePasteSync;
  }, [handlePasteSync]);

  const config = useMemo(
    () => ({
      readonly: false,
      height: 400,
      toolbar: false,
      statusbar: false,
      disablePlugins: "media", 
      media: { mediaInFakeBlock: false },
      uploader: { insertImageAsBase64URI: false },
      link: { processPastedLink: false }, // Double protection against auto-linking
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      events: {
        paste: (e: ClipboardEvent) => pasteHandlerRef.current?.(e)
      }
    }),
    []
  );

  return (
    <JoditEditorImport
      editorRef={(instance) => {
        joditInstance.current = instance;
      }}
      value={value}
      config={config}
      onBlur={(newContent: string) => onChange?.(newContent)}
    />
  );
};